import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles } from "lucide-react";
import { useLocale } from "@/i18n/LocaleProvider";
import { scrollToSection } from "@/lib/scrollToSection";

/**
 * Mobile-only sticky action bar.
 *
 * Between the hero and the CTA section at the very bottom there is no
 * persistent way to convert on a small screen. This bar appears once the hero
 * is scrolled past and keeps the two primary actions one tap away.
 */
const MobileStickyCTA = () => {
  const { t } = useTranslation();
  const { localize } = useLocale();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.9);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-md px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] transition-transform duration-300 ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
      aria-hidden={!visible}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => scrollToSection("blueprint")}
          className="flex-1 inline-flex justify-center items-center gap-2 h-12 btn-clip bg-card/60 text-foreground font-semibold text-sm active:scale-[0.97] transition-transform"
          tabIndex={visible ? 0 : -1}
        >
          <Sparkles size={16} className="text-accent" />
          {t("stickyCta.secondary")}
        </button>
        <button
          type="button"
          onClick={() => navigate(localize("/contact"))}
          className="flex-1 inline-flex justify-center items-center gap-2 h-12 btn-clip bg-primary text-primary-foreground btn-label-contrast font-semibold text-sm active:scale-[0.97] transition-transform"
          tabIndex={visible ? 0 : -1}
        >
          {t("stickyCta.primary")}
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default MobileStickyCTA;
